/**
 * Public Visitor API — reads from v2 tables via apiClient.
 */
import { apiOrigin } from '../utils/surface';

// ─── Public types ───────────────────────────────────────────────────────────

export interface VisitorProperty {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
  telnyx_number?: string;
  logo_url?: string;
  greeting_message?: string;
  display_style?: 'modern_dark' | 'modern_light' | 'classic';
  directory_display?: 'tenant_name' | 'unit_only' | 'display_name';
  visitor_enabled?: boolean;
  accent_color?: string;
  status: string;
}

export interface VisitorTenant {
  id: string;            // person_id (UUID)
  first_name: string;
  last_name: string;
  person_type?: string;  // 'tenant' | 'subtenant' | …
  is_primary?: boolean;
  /// True when the tenant has activated the mobile app (people.activation_id
  /// is non-null on the canonical row). Used by the visitor directory to
  /// hide the Video Call button for tenants who can't receive a VoIP push.
  activated?: boolean;
  /// Per-property display prefs sourced from /intercom/display-prefs.
  /// Null when the tenant hasn't touched the iOS toggle.
  display_prefs?: {
    show_name?: boolean | null;
    show_photo?: boolean | null;
    name_source?: 'profile' | 'custom' | null;
    custom_name?: string | null;
    photo_source?: 'profile' | 'custom' | null;
    custom_photo?: string | null;
  } | null;
}

export interface VisitorUnit {
  id: number;
  unit_number: string;
  floor?: string;
  property_id: number;
  display_name?: string;
  status: string;
  tenant_name?: string;       // Populated based on directory_display (legacy)
  tenants?: VisitorTenant[];  // One entry per occupant — directory renders a card per person
  has_intercom?: boolean;     // Whether this unit has a callable intercom
  dial_code?: string;         // Numeric DTMF proxy for letter-bearing unit numbers (e.g. "1011" for "101A")
}

export interface VisitorSettings {
  directory_display: 'tenant_name' | 'unit_only' | 'display_name';
  greeting_message: string;
  display_style: 'modern_dark' | 'modern_light' | 'classic';
  accent_color: string;
  background_color?: string;
  logo_url?: string;
  show_phone: boolean;
  allow_video_call: boolean;
  allow_phone_call: boolean;
  telnyx_number?: string;
  show_floors?: boolean;
  // Access control settings
  pin_access_enabled?: boolean;
  guest_pin_access_enabled?: boolean;
  face_access_enabled?: boolean;
  private_directory?: boolean;
  show_powered_by?: boolean;
  custom_footer_text?: string;
  auto_lock_enabled?: boolean;
  auto_lock_start?: string;  // "HH:MM" 24-hour
  auto_lock_end?: string;    // "HH:MM" 24-hour
  require_visitor_name?: boolean;
  require_visitor_phone?: boolean;
  // Directory display tweaks the PM controls in Settings → Branding & Display.
  hide_vacant_units?: boolean;
  show_floor_grouping?: boolean;
  show_phone_on_directory?: boolean;
  show_tenant_count?: boolean;
  custom_no_answer_text?: string;
  unit_sort_order?: string;
  directory_name_format?: string;
  logo_size_px?: number;
  visitor_code_response?: 'grant_only' | 'grant_and_open_pass';
}

interface Tenant {
  id: number;
  property_id: number;
  unit_id?: number;
  first_name: string;
  last_name: string;
  is_primary?: boolean;
}

// ─── API calls ──────────────────────────────────────────────────────────────

/**
 * Get a property's public visitor profile by ID or UUID.
 * If propertyId contains dashes it is treated as a UUID and matched against property_uuid.
 */
export const getVisitorProperty = async (propertyId: number | string): Promise<VisitorProperty> => {
  const response = await fetch(`${apiOrigin()}/visitor/property/${encodeURIComponent(String(propertyId))}`);
  if (!response.ok) throw new Error('Property not found');
  return response.json();
};

/**
 * Get a property's public visitor profile by slug (URL-friendly name).
 */
export const getVisitorPropertyBySlug = async (slug: string): Promise<VisitorProperty> => {
  return getVisitorProperty(slug);
};

/**
 * Get public units for a property (visitor directory).
 */
export const getVisitorUnits = async (propertyId: number | string): Promise<VisitorUnit[]> => {
  const property = await getVisitorProperty(propertyId);
  const response = await fetch(`${apiOrigin()}/visitor/property/${encodeURIComponent(String(property.id))}/units`);
  if (!response.ok) throw new Error('Visitor directory unavailable');
  const result = await response.json();
  const rows = Array.isArray(result) ? result : result?.units || result?.tenants || [];
  return rows.map((row: any) => ({
    ...row,
    id: row.unit_id || row.id,
    property_id: property.id,
    unit_number: String(row.unit_number || row.unit_name || row.label || ''),
    tenant_name: row.tenant_name || row.display_name || undefined,
    tenants: Array.isArray(row.tenants) ? row.tenants : [],
  }));
};

/**
 * Get the visitor settings for a property.
 */
export const getVisitorSettings = async (propertyId: number | string): Promise<VisitorSettings> => {
  try {
    // Try to get property-specific settings from the property record
    const property = await getVisitorProperty(propertyId);
    const p = property as any;
    return {
      directory_display: property.directory_display || 'tenant_name',
      greeting_message: property.greeting_message || 'Welcome! Please select the unit you\'re visiting.',
      display_style: property.display_style || 'modern_dark',
      accent_color: property.accent_color || '#4f46e5',
      background_color: p.background_color || undefined,
      logo_url: property.logo_url,
      show_phone: !!property.phone,
      // Postgres columns are singular; the legacy plural keys never existed
      // server-side. Read singular as primary, fall back to plural in case any
      // other writer ever populated those.
      allow_video_call: (p.allow_video_call ?? p.allow_video_calls) !== false,
      allow_phone_call: (p.allow_phone_call ?? p.allow_phone_calls) !== false && !!property.telnyx_number,
      telnyx_number: property.telnyx_number,
      show_floors: true,
      // Access control settings — read from property record, default permissive
      pin_access_enabled: p.pin_access_enabled !== false,
      guest_pin_access_enabled: p.guest_pin_access_enabled !== false,
      face_access_enabled: p.face_access_enabled === true || p.face_access_available === true,
      private_directory: !!p.private_directory,
      show_powered_by: p.show_powered_by !== false,
      custom_footer_text: p.custom_footer_text || undefined,
      auto_lock_enabled: !!p.auto_lock_enabled,
      auto_lock_start: p.auto_lock_start || undefined,
      auto_lock_end: p.auto_lock_end || undefined,
      require_visitor_name: !!p.require_visitor_name,
      require_visitor_phone: !!p.require_visitor_phone,
      hide_vacant_units: !!p.hide_vacant_units,
      show_floor_grouping: !!p.show_floor_grouping,
      show_phone_on_directory: !!p.show_phone_on_directory,
      show_tenant_count: !!p.show_tenant_count,
      custom_no_answer_text: p.custom_no_answer_text || undefined,
      unit_sort_order: p.unit_sort_order || undefined,
      directory_name_format: p.directory_name_format || undefined,
      logo_size_px: p.logo_size_px ? Number(p.logo_size_px) : undefined,
    };
  } catch {
    // Return defaults if property not found
    return {
      directory_display: 'tenant_name',
      greeting_message: 'Welcome! Please select the unit you\'re visiting.',
      display_style: 'modern_dark',
      accent_color: '#4f46e5',
      show_phone: false,
      allow_video_call: true,
      allow_phone_call: false,
    };
  }
};

// ─── Visitor session (§28.518.99.54.c-1) ───────────────────────────────────
// Module-scope cache: propertyId → session token metadata. Kept invisible
// to the visual layer — callers only interact via visitorActionCall().

const AWS_BASE = apiOrigin();
type VisitorSessionEntry = { token: string; expiresAt: number };
const visitorSessions: Map<string, VisitorSessionEntry> = new Map();

/**
 * Explicitly mint a fresh visitor session for the given property. Cached in
 * module scope. Safe to call repeatedly — later calls overwrite the cache
 * (used by the 401-retry path in visitorActionCall).
 */
export async function ensureVisitorSession(propertyId: number | string): Promise<VisitorSessionEntry> {
  const pid = String(propertyId);
  const r = await fetch(`${AWS_BASE}/visitor/session/issue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ property_id: pid }),
  });
  if (!r.ok) throw new Error(`visitor_session_issue_failed_${r.status}`);
  const j = await r.json();
  const token = (j && (j.visitor_session_token || j.token)) as string;
  const exp = j && j.session_expires_at ? Date.parse(String(j.session_expires_at)) : (Date.now() + 3300 * 1000);
  const entry: VisitorSessionEntry = { token, expiresAt: exp };
  visitorSessions.set(pid, entry);
  return entry;
}

/**
 * Invoke a /visitor/action/* endpoint with the cached session token. Mints a
 * session on demand and does a single 401 retry with a freshly-minted token.
 */
export async function visitorActionCall<T = any>(
  propertyId: number | string,
  path: string,
  body: any = {}
): Promise<T> {
  const pid = String(propertyId);
  let session = visitorSessions.get(pid);
  if (!session || (session.expiresAt && session.expiresAt < Date.now() + 30 * 1000)) {
    session = await ensureVisitorSession(pid);
  }
  const doFetch = async (tok: string) => fetch(`${AWS_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
    body: JSON.stringify(body || {}),
  });
  let res = await doFetch(session.token);
  if (res.status === 401) {
    session = await ensureVisitorSession(pid);
    res = await doFetch(session.token);
  }
  const j: unknown = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(`visitor_action_failed_${res.status}`), { status: res.status, body: j });
  return j as T;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
